import { useState } from "react";
import { Button } from "../components/common/Button";
import { Card } from "../components/common/Card";
import { Field } from "../components/common/Field";
import { onboardingApi, forgetSignupCode, rememberedSignupCode } from "../services/onboardingApi";
import { useToast } from "../context/ToastContext";
import { useFlow } from "../hooks/useFlow";
import { completeFlow } from "../services/flowTelemetry";
import { FLOWS } from "../services/flows";

/**
 * The first five minutes (#222).
 *
 * A brand-new account has no sites, no hives and no history, so without
 * this every screen is an empty state at once and the app looks broken
 * rather than new.
 *
 * Two questions, because a hive is what everything else in this app hangs
 * off. Not a multi-step wizard: the shorter this is, the more people come
 * out the other side of it.
 *
 * No sample data is offered, deliberately -- see services/onboarding.py.
 * Everything created here is something the person said they have.
 */
export function FirstRunPage({ hiveLimit, onDone }) {
  const { notify } = useToast();
  /* The most expensive flow in the product to abandon: somebody who
     gives up here never reaches the app at all. */
  const flow = useFlow(FLOWS.ONBOARDING);
  const [siteName, setSiteName] = useState("");
  const [hiveCount, setHiveCount] = useState("");
  const [code, setCode] = useState(rememberedSignupCode);
  // Collapsed unless a code came in on the link. Registration friction is
  // what kills conversion, and this box must never look like a step.
  const [codeOpen, setCodeOpen] = useState(() => Boolean(rememberedSignupCode()));
  const [busy, setBusy] = useState(false);

  async function submit(skipped) {
    setBusy(true);
    try {
      const r = await onboardingApi.complete({
        skipped,
        site_name: skipped ? undefined : siteName.trim() || undefined,
        hive_count: skipped ? undefined : Number(hiveCount) || 0,
        acquired_via_code: code.trim() || undefined,
      });
      forgetSignupCode();
      /* SKIPPING IS NOT A COMPLETION, and that is a judgment call worth
         stating. The endpoint is the same call either way, so it would
         be easy to count both -- but the question this flow exists to
         answer is "how many new accounts never got a hive in", and
         somebody who skipped setup did not get set up. Recording it as a
         completion would make the onboarding funnel look perfect while
         the accounts behind it sit empty.

         It is still not left to the unmount path, because skipping is a
         DELIBERATE exit the app offered rather than somebody wandering
         off, and recording it here keeps its duration honest.

         AND COUNTED SEPARATELY (4 Oct), because "abandoned" otherwise
         covers two groups with different fixes -- people we lost, and
         people who will come back to it. The skip marker makes the
         difference a subtraction rather than a guess. See FLOWS. */
      if (skipped) {
        flow.abandoned();
        completeFlow(FLOWS.ONBOARDING_SKIPPED);
      } else {
        flow.finished();
      }
      // Said plainly rather than silently doing less than they asked. They
      // told us they have more hives than the free plan records; finding
      // that out by counting rows later is worse than being told now.
      if (r.capped_at != null) {
        notify(
          `Added ${r.capped_at} hives — that's what a free account covers. You can upgrade any time to add the rest.`,
          "warning",
        );
      }
      onDone(r);
    } catch (err) {
      notify(err.apiMessage || "Could not save that. Try again.", "danger");
      setBusy(false);
    }
  }

  return (
    <div style={{ maxWidth: "34rem", margin: "0 auto", padding: "var(--space-4)" }}>
      <Card title="Welcome to Bee Keeper">
        <p style={{ color: "var(--color-text-muted)" }}>
          Two quick questions and your apiary is set up. You can change any
          of it later.
        </p>

        <div style={{ display: "grid", gap: "var(--space-3)", marginTop: "var(--space-3)" }}>
          <Field label="Where are your bees?" hint="The yard or site name — whatever you call it.">
            {(id) => (
              <input
                id={id}
                value={siteName}
                onChange={(e) => setSiteName(e.target.value)}
                placeholder="e.g. Home Yard"
                maxLength={100}
                className="touch-target"
                style={{ width: "100%" }}
              />
            )}
          </Field>

          <Field
            label="How many hives are there?"
            hint={hiveLimit ? `A free account records up to ${hiveLimit}.` : undefined}
          >
            {(id) => (
              <input
                id={id}
                type="number"
                min="0"
                max="500"
                value={hiveCount}
                onChange={(e) => setHiveCount(e.target.value)}
                placeholder="e.g. 4"
                className="touch-target"
                style={{ width: "100%" }}
              />
            )}
          </Field>

          {codeOpen ? (
            <Field label="Referral or discount code" hint="Optional.">
              {(id) => (
                <input
                  id={id}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  maxLength={16}
                  className="touch-target"
                  style={{ width: "100%" }}
                />
              )}
            </Field>
          ) : (
            <button
              type="button"
              onClick={() => setCodeOpen(true)}
              style={{
                background: "none", border: "none", padding: 0,
                color: "var(--color-text-muted)", textDecoration: "underline",
                cursor: "pointer", justifySelf: "start",
              }}
            >
              Have a code?
            </button>
          )}
        </div>

        <div style={{ display: "flex", gap: "var(--space-2)", marginTop: "var(--space-4)" }}>
          <Button onClick={() => submit(false)} disabled={busy || !siteName.trim()}>
            {busy ? "Setting up..." : "Set up my apiary"}
          </Button>
          {/* One tap, always available. Somebody moving forty hives across
              from another system wants their import, not a form. */}
          <Button variant="secondary" onClick={() => submit(true)} disabled={busy}>
            Skip for now
          </Button>
        </div>
      </Card>
    </div>
  );
}
